import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import stockService from '../services/stockService';

/**
 * Custom hook for managing stock movements data
 * @returns {Object} Stock movements state and handlers
 */
const useStockMovements = () => {
  const { t } = useTranslation('hooks');

  // State for stock movements table
  const [loading, setLoading] = useState(false);
  const [movements, setMovements] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalMovements, setTotalMovements] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [reasonFilter, setReasonFilter] = useState('');

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    increases: 0,
    decreases: 0,
  });

  // Reasons
  const [reasons, setReasons] = useState([]);

  // Fetch stock movements based on current filters
  const fetchStockMovements = useCallback(async () => {
    try {
      setLoading(true);

      // Prepare query parameters
      const params = {
        page: page + 1,
        limit: rowsPerPage,
        search: searchQuery,
        reason: reasonFilter || undefined,
      };

      const data = await stockService.getAllStockMovements(params);

      setMovements(data.movements || []);
      setTotalMovements(data.pagination?.totalItems || 0);
    } catch (error) {
      console.error('Error fetching stock movements:', error);
      toast.error(t('stockMovements.loadError'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchQuery, reasonFilter, t]);

  // Fetch stock movements stats
  const fetchStats = useCallback(async () => {
    try {
      const data = await stockService.getStockMovementStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching stock movement stats:', error);
    }
  }, []);

  // Initialize reasons
  useEffect(() => {
    const getReasons = () => {
      const reasons = stockService.getStockMovementReasons();
      setReasons(reasons);
    };

    getReasons();
  }, []);

  // Fetch data when filters change
  useEffect(() => {
    fetchStockMovements();
  }, [fetchStockMovements]);

  // Fetch stats on mount
  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filter handlers
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPage(0);
  };

  const handleReasonFilter = (reason) => {
    setReasonFilter(reason);
    setPage(0);
  };

  // Refresh data
  const refreshData = () => {
    fetchStockMovements();
    fetchStats();
  };

  // Get readable reason text
  const getReasonText = (reason) => {
    const reasonMap = {
      order_placement: t('stockMovements.reasons.order_placement'),
      order_cancellation: t('stockMovements.reasons.order_cancellation'),
      order_cancellation_user: t('stockMovements.reasons.order_cancellation_user'),
      return_approved: t('stockMovements.reasons.return_approved'),
      return_restocked: t('stockMovements.reasons.return_restocked'),
      manual_update: t('stockMovements.reasons.manual_update'),
      initial_stock: t('stockMovements.reasons.initial_stock'),
    };

    return reasonMap[reason] || reason;
  };

  return {
    // State
    loading,
    movements,
    page,
    rowsPerPage,
    totalMovements,
    searchQuery,
    reasonFilter,
    stats,
    reasons,

    // Handlers
    handleChangePage,
    handleChangeRowsPerPage,
    handleSearch,
    handleReasonFilter,
    refreshData,
    getReasonText,
  };
};

export default useStockMovements;
